import { Stack, Switch, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

interface StyledSwitchProps {
  value: boolean;
  onChange: (value: boolean) => void;
  label: string;
  disabled?: boolean;
}

export default function StyledSwitch({ value, onChange, label, disabled = false }: StyledSwitchProps) {
  const theme = useTheme();

  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={{
        padding: '6px 8px',
        borderRadius: '6px',
        transition: 'background-color 0.2s ease-in-out',
        cursor: disabled ? 'default' : 'pointer',
        '&:hover': {
          backgroundColor: disabled ? 'transparent' : 'rgba(255, 255, 255, 0.08)'
        }
      }}
      onClick={disabled ? undefined : () => onChange(!value)}>
      <Switch
        checked={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        size="medium"
        sx={{
          width: 52,
          height: 32,
          padding: 0,
          '& .MuiSwitch-switchBase': {
            padding: 0,
            margin: '2px',
            transitionDuration: '300ms',
            '&.Mui-checked': {
              transform: 'translateX(20px)',
              '& + .MuiSwitch-track': {
                backgroundColor: theme.palette.primary.main,
                opacity: 1,
                border: 0
              },
              '& .MuiSwitch-thumb': {
                backgroundColor: '#fff'
              },
              '&.Mui-disabled + .MuiSwitch-track': {
                opacity: 0.5,
                backgroundColor: theme.palette.primary.main
              },
              '&.Mui-disabled .MuiSwitch-thumb': {
                backgroundColor: '#f5f5f5'
              }
            },
            '&.Mui-focusVisible .MuiSwitch-thumb': {
              border: `6px solid ${theme.palette.primary.main}40`
            },
            '&.Mui-disabled .MuiSwitch-thumb': {
              backgroundColor: disabled && value ? '#f5f5f5' : '#e0e0e0'
            },
            '&.Mui-disabled + .MuiSwitch-track': {
              opacity: 0.3,
              backgroundColor: disabled && value ? theme.palette.primary.main : 'rgba(255, 255, 255, 0.3)'
            }
          },
          '& .MuiSwitch-thumb': {
            boxSizing: 'border-box',
            width: 28,
            height: 28,
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
            backgroundColor: '#fff',
            border: '1px solid rgba(0, 0, 0, 0.1)'
          },
          '& .MuiSwitch-track': {
            borderRadius: 16,
            backgroundColor: 'rgba(255, 255, 255, 0.3)',
            opacity: 1,
            border: '1px solid rgba(255, 255, 255, 0.2)',
            transition: theme.transitions.create(['background-color'], {
              duration: 300
            })
          }
        }}
      />
      <Typography
        sx={{
          fontSize: '14px',
          fontWeight: 500,
          userSelect: 'none',
          color: disabled ? 'rgba(255, 255, 255, 0.5)' : theme.palette.primary.contrastText,
          opacity: disabled ? 0.6 : 1
        }}>
        {label}
      </Typography>
    </Stack>
  );
}
